'use client';

import NextImage from 'next/image';
import { type ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';

import styles from './image.component.module.scss';

type BaseImageOwnProps = {
  alt: string;
  /**
   * 画像本来のaspect-ratioを使用します。
   */
  isIntrinsicAspectRatio?: boolean;
  /**
   * 画像を親要素いっぱいに表示します。falseの場合はwidth属性の値を使用します。
   */
  fullWidth?: boolean;
  disableHeightAuto?: boolean;
};
type NextImageProps = {
  /**
   * フレームワークのImageコンポーネントかweb標準のimg要素を使用します。
   */
  isImgElement?: false;
} & ComponentPropsWithoutRef<typeof NextImage>;
type StandardImageProps = {
  /**
   * フレームワークのImageコンポーネントかweb標準のimg要素を使用します。
   */
  isImgElement: true;
} & Omit<ComponentPropsWithoutRef<'img'>, 'alt'>;

type BaseImageProps = BaseImageOwnProps & (NextImageProps | StandardImageProps);

export const Image = forwardRef<HTMLImageElement, BaseImageProps>(
  (
    {
      alt,
      isImgElement,
      isIntrinsicAspectRatio,
      fullWidth = true,
      disableHeightAuto,
      src,
      width,
      height,
      className,
      ...rest
    },
    ref
  ) => {
    return isImgElement ? (
      <img
        alt={alt}
        className={[className, styles.img].filter(Boolean).join(' ')}
        data-disable-height-auto={disableHeightAuto}
        data-full-width={fullWidth}
        data-is-intrinsic-aspect-ratio={isIntrinsicAspectRatio}
        height={height}
        ref={ref}
        src={src}
        width={width}
        {...rest}
      />
    ) : (
      <NextImage
        alt={alt}
        className={[className, styles.img].filter(Boolean).join(' ')}
        data-disable-height-auto={disableHeightAuto}
        data-full-width={fullWidth}
        data-is-intrinsic-aspect-ratio={isIntrinsicAspectRatio}
        height={height}
        ref={ref}
        src={src}
        width={width}
        {...rest}
      />
    );
  }
);
Image.displayName = 'Image';
